import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AttachmentImage } from "@/components/attachment-image";
import { ImageLightbox } from "@/components/image-lightbox";
import { api, attachmentDownloadUrl } from "@/lib/api";
import { photoCaption } from "@/lib/photo-label";

interface PhotoAttachment {
    id: string;
    file_name: string;
    photo_tag: string | null;
    /** Server label from the device the photo shows (size, type, serial, shot). */
    photo_label?: string | null;
    created_at: string;
    uploaded_by_name: string | null;
}

/**
 * Office-side job photos gallery (Issue: dave-photos-visibility).
 *
 * Field techs upload general job photos via the field app as
 * `job_attachments` rows with `category='photo'`. The office Job page never
 * queried that category, so a tech's on-site photos only showed as filenames
 * in the Activity feed ("Uploaded 3 photos") with no way to view the images.
 * This card is the missing office surface: it fetches `category='photo'`
 * attachments and renders a thumbnail grid with a full-screen lightbox,
 * mirroring the field app's Photos card (reference-column.tsx).
 *
 * View-only by design — office photo *backfill* has its own module
 * (/jobs/photo-backlog); this card just makes the field's photos visible.
 * Renders nothing when the job has no photos, matching the page's other
 * content-gated cards (PO, parts, replacements).
 */
export function JobPhotosCard({ jobId }: { jobId: string }) {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    const photosQ = useQuery({
        queryKey: ["job-photos", jobId],
        queryFn: () => api.get<{ attachments: PhotoAttachment[] }>(`/api/attachments?jobId=${jobId}&category=photo`),
        enabled: !!jobId,
    });

    const photos = photosQ.data?.attachments ?? [];
    if (photos.length === 0) return null;

    const subtitle = (p: PhotoAttachment) => {
        const who = p.uploaded_by_name ? `Uploaded by ${p.uploaded_by_name}` : "Uploaded";
        return `${who} · ${new Date(p.created_at).toLocaleDateString()}`;
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" /> Photos
                    <Badge variant="secondary" className="text-[10px]">
                        {photos.length}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                    {photos.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
                            onClick={() => setLightboxIndex(i)}
                            title={`${photoCaption(p)} — ${subtitle(p)}`}
                        >
                            <AttachmentImage
                                attachmentId={p.id}
                                alt={photoCaption(p)}
                                className="h-full w-full object-cover"
                                loading="lazy"
                            />
                            <span className="absolute inset-x-0 bottom-0 line-clamp-2 bg-black/60 px-1.5 py-0.5 text-left text-[10px] leading-tight text-white">
                                {photoCaption(p)}
                            </span>
                        </button>
                    ))}
                </div>
            </CardContent>
            {lightboxIndex !== null && (
                <ImageLightbox
                    photos={photos.map((p) => ({
                        src: attachmentDownloadUrl(p.id, { disposition: "inline" }),
                        alt: photoCaption(p),
                        caption: photoCaption(p),
                        downloadHref: attachmentDownloadUrl(p.id, { disposition: "attachment" }),
                    }))}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </Card>
    );
}
